import { type Nullable } from 'corgi-types';
import {
  ArticleLabelType,
  type Article,
  type ArticleBrand,
  type ArticleBreadcrumb,
  type ArticleDeliveryTime,
  type ArticlePrices,
  type ArticleRegularPrice,
} from 'graphql-ts-types';

import type { AddToCartPlacement } from '../enums/Tracking';
import type { ArticleBestOffer, ArticleTracking } from '../types/Article';
import type { ArticleLabelsList } from '../types/labels';
import type { ProductCampaign } from '../types/product-card';
import { filterDiscountLabels, getLabelStringValue } from '../utils/labels';

type ArticlePrice = Pick<ArticleRegularPrice, 'value'>;

interface ArticleDelivery {
  time?: Pick<ArticleDeliveryTime, 'numberOfDays'>;
}

export type ArticleCampaigns = Array<Nullable<Pick<ProductCampaign, 'key'>>>;

export interface TrackableArticle extends Pick<
  Article,
  'sku' | 'name' | 'variantName' | 'parentSku'
> {
  labels_v2?: ArticleLabelsList;
  prices: {
    regular: Nullable<Pick<ArticlePrice, 'value'>>;
    special: Nullable<Pick<ArticlePrice, 'value'>>;
    currency?: ArticlePrices['currency'];
  };
  delivery?: Nullable<ArticleDelivery>;
  campaigns?: ArticleCampaigns;
  breadcrumbs?: Array<{
    name: ArticleBreadcrumb['name'];
    url?: ArticleBreadcrumb['url'];
  }>;
  brand?: Nullable<Pick<ArticleBrand, 'name'>>;
  productShippingCharge?: Nullable<number>;
  productServiceCharge?: Nullable<number>;
  productEfficiencyValue?: Nullable<number>;
  bestOffer?: ArticleBestOffer;
  placement?: AddToCartPlacement;
  trackingId?: Article['trackingId'];
  sponsored?: Nullable<{ owner?: Nullable<string> }>;
}

const formatPrice = (value: number) => value / 100;

const transformCategoryBreadcrumb = (breadcrumbs: TrackableArticle['breadcrumbs'] = []) =>
  breadcrumbs
    ?.filter(({ url }) => !url || url !== '/')
    .map(({ name }) => name)
    .join(' | ');

const transformArticleCategories = (breadcrumbs: TrackableArticle['breadcrumbs'] = []) =>
  breadcrumbs.map(category => category.name);

const transformProductPrice = (prices: TrackableArticle['prices']) =>
  prices?.special?.value ?? prices?.regular?.value ?? 0;

/**
 * Takes article data and returns an object with the properties needed for tracking.
 *
 * {@link https://github.com/Home24/GraphQL-Api/blob/3b037fedaf4b09399ae9d9a2eb16b71862ce4049/internal/pkg/resolvers/article.go#L957 GQL article resolver}
 */
// eslint-disable-next-line complexity
export const articleToTrackingProps = (article: TrackableArticle): ArticleTracking => {
  const productPrice = transformProductPrice(article.prices);
  const categoryBreadcrumb = transformCategoryBreadcrumb(article.breadcrumbs);
  const productCampaigns = article.campaigns?.map(campaign => campaign.key) ?? [];

  const filteredLabels = filterDiscountLabels(
    article.labels_v2,
    article.prices?.regular?.value ?? 0
  );

  const productLabels =
    filteredLabels?.map(label => {
      if (!label?.value || !label?.type) return '';

      return getLabelStringValue(label.value, label.type);
    }) ?? [];
  const productCategories = transformArticleCategories(article.breadcrumbs);
  const isHome24Tipp = !!article.labels_v2?.some(
    label => label?.type === ArticleLabelType.Home24Selection
  );

  return {
    productPrice,
    productPriceCurrency: article.prices.currency,
    categoryBreadcrumb,
    productCampaigns,
    productLabels,
    productBrand: article.brand?.name ?? '',
    productDeliveryTime: article.delivery?.time?.numberOfDays ?? 0,
    productName: article.name,
    productSimpleSKU: article.sku,
    resultId: article.trackingId,
    variantName: article.variantName,
    productConfigSKU: article.parentSku,
    ...(article.placement && { placement: article.placement }),
    ...(article.productShippingCharge && {
      productShippingCharge: formatPrice(article.productShippingCharge),
    }),
    ...(article.productServiceCharge && {
      productServiceCharge: formatPrice(article.productServiceCharge),
    }),
    ...(article.productEfficiencyValue && {
      productEfficiencyValue: article.productEfficiencyValue,
    }),
    ...(article.bestOffer?.shop?.id && {
      shopId: article.bestOffer.shop.id,
    }),
    productCategories,
    isHome24Tipp,
  };
};
